import { useState, createContext, useContext, type ReactNode } from 'react';
import clsx from 'clsx';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
}

export interface TabsProps {
  defaultTab: string;
  children: ReactNode;
  className?: string;
  onChange?: (tab: string) => void;
}

export function Tabs({ defaultTab, children, className, onChange }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    onChange?.(tab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabListProps {
  children: ReactNode;
  className?: string;
  variant?: 'underline' | 'pills' | 'enclosed';
}

export function TabList({ children, className, variant = 'underline' }: TabListProps) {
  const variants = {
    underline: 'border-b border-slate-200 dark:border-slate-800 flex gap-6',
    pills: 'bg-slate-100 dark:bg-slate-800/60 rounded-xl p-1 flex gap-1',
    enclosed: 'border-b border-slate-200 dark:border-slate-800 flex',
  };

  return <div className={clsx(variants[variant], className)}>{children}</div>;
}

export interface TabProps {
  value: string;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  count?: number;
  variant?: 'underline' | 'pills' | 'enclosed';
}

export function Tab({ value, children, className, icon, count, variant = 'underline' }: TabProps) {
  const { activeTab, setActiveTab } = useTabs();
  const isActive = activeTab === value;

  const variants = {
    underline: clsx(
      'relative pb-3 text-sm font-medium transition-colors',
      isActive ? 'text-primary-500' : 'text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100',
      isActive && 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary-400 after:rounded-full'
    ),
    pills: clsx(
      'px-4 py-2 text-sm font-medium rounded-lg transition-all',
      isActive
        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100'
    ),
    enclosed: clsx(
      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
      isActive
        ? 'border-primary-400 text-primary-500 bg-white dark:bg-slate-900'
        : 'border-transparent text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-700'
    ),
  };

  return (
    <button onClick={() => setActiveTab(value)} className={clsx(variants[variant], 'flex items-center gap-2', className)}>
      {icon}
      {children}
      {count !== undefined && (
        <span
          className={clsx(
            'px-2 py-0.5 text-xs rounded-full',
            isActive
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300'
              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200'
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  const { activeTab } = useTabs();

  if (activeTab !== value) return null;

  return <div className={clsx('animate-fade-in', className)}>{children}</div>;
}

